import React, { useState } from 'react';
import { Search, Loader2, CheckCircle } from 'lucide-react';
import { useI18n } from '@/contexts/I18nContext';
import { integrationsAPI } from '@/services/api';
import { toast } from 'sonner';

/**
 * Icelandic kennitala input with an optional "Sækja í þjóðskrá" button.
 * - Clamps to 10 digits (display formatted as "123456 7890")
 * - When 10 digits entered and onFetched is provided, a lookup button appears.
 *   The button calls the backend's national-registry proxy (ja.is); if the
 *   backend returns 503 "not_configured", we show a soft toast instead of an
 *   error. If the company data comes back, onFetched(data) is called so the
 *   parent can autofill name/address/email.
 */
const formatKennitala = (raw) => {
  const digits = (raw || '').replace(/\D/g, '').slice(0, 10);
  if (digits.length <= 6) return digits;
  return digits.slice(0, 6) + ' ' + digits.slice(6);
};

const parseKennitala = (v) => (v || '').replace(/\D/g, '').slice(0, 10);

const KennitalaInput = ({
  value,
  onChange,            // (formattedValue) => void
  onFetched,           // optional (companyData) => void — triggers Sækja button
  onFailed,            // optional () => void — called when lookup returns not_found/not_configured/error
  placeholder = '123456 7890',
  label,
  testIdPrefix = 'kennitala',
  disabled = false,
  className = '',
  showLookup = true,
}) => {
  const { lang } = useI18n();
  const is = lang === 'is';
  const [loading, setLoading] = useState(false);
  const [lastFetched, setLastFetched] = useState(null);

  const digits = parseKennitala(value);
  const canFetch = showLookup && !!onFetched && digits.length === 10 && !loading && !disabled;

  const handleChange = (e) => {
    onChange?.(formatKennitala(e.target.value));
    if (lastFetched && parseKennitala(e.target.value) !== parseKennitala(lastFetched)) {
      setLastFetched(null);
    }
  };

  const handleFetch = async () => {
    if (!canFetch) return;
    setLoading(true);
    try {
      const r = await integrationsAPI.nationalRegistryBusiness(digits);
      const data = r?.data || {};
      if (!data.ok) {
        const msg = data.error === 'not_configured'
          ? (is ? 'Uppfletting er ekki stillt enn. Biddu um ja.is aðgang.' : 'Lookup not configured. Request a ja.is API key.')
          : data.error === 'not_found'
            ? (is ? 'Engin færsla fannst með þessa kennitölu.' : 'No record found with that kennitala.')
            : (is ? 'Gat ekki sótt upplýsingar.' : 'Lookup failed.');
        toast.info(msg);
        onFailed?.();
      } else {
        setLastFetched(value);
        onFetched?.(data);
        const label = data.kind === 'person' ? (is ? 'Einstaklingur' : 'Person') : (is ? 'Fyrirtæki' : 'Business');
        toast.success(is ? `${label}: ${data.name}` : `${label}: ${data.name}`);
      }
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(detail || (is ? 'Gat ekki sótt upplýsingar.' : 'Lookup failed.'));
      onFailed?.();
    }
    setLoading(false);
  };

  return (
    <div className={className}>
      {label && (
        <label className="text-xs text-foreground-muted mb-1 block" data-testid={`${testIdPrefix}-label`}>
          {label}
        </label>
      )}
      <div className="flex gap-2">
        <input
          type="text"
          inputMode="numeric"
          value={value}
          onChange={handleChange}
          onPaste={(e) => {
            // Clean paste — strip non-digits first
            const pasted = (e.clipboardData || window.clipboardData)?.getData('text') || '';
            if (/\D/.test(pasted)) {
              e.preventDefault();
              onChange?.(formatKennitala(pasted));
            }
          }}
          maxLength={11}            // 10 digits + 1 separator
          placeholder={placeholder}
          className="font-mono flex-1"
          disabled={disabled}
          data-testid={`${testIdPrefix}-input`}
        />
        {showLookup && onFetched && (
          <button
            type="button"
            onClick={handleFetch}
            disabled={!canFetch}
            className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-md border border-border hover:bg-card-hover disabled:opacity-40 disabled:cursor-not-allowed whitespace-nowrap transition-colors"
            title={is ? 'Sækja fyrirtækisupplýsingar úr fyrirtækjaskrá' : 'Fetch company info from the company registry'}
            data-testid={`${testIdPrefix}-fetch-btn`}
          >
            {loading
              ? <Loader2 size={12} className="animate-spin" />
              : lastFetched
                ? <CheckCircle size={12} className="text-emerald-400" />
                : <Search size={12} />}
            {is ? 'Sækja í fyrirtækjaskrá' : 'Fetch from registry'}
          </button>
        )}
      </div>
    </div>
  );
};

export { KennitalaInput, formatKennitala, parseKennitala };
export default KennitalaInput;
